import { AppNS } from 'app';
import { UI_DATE_FORMAT_STRING } from 'app/helpers/parseDate';
import PermitsInfo from 'app/main/common/bookings/permits-info/PermitsInfo';
import { ExternalButton } from 'app/ui/components/external-button/ExternalButton';
import ViewIcon from 'app/ui/icons/ViewIcon';
import { getHourFormatted } from 'app/ui/modules/job-events/eventsHelper';
import { parseDate } from 'app/ui_v2/helpers/parseDate';
import { unescape } from 'lodash-es';

import { Box, Card, SxProps, Theme, useTheme } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { getInspectionExternalLink, getProjectExternalLink } from '../../config';
import Geolocation from '../geolocation/Geolocation';
import Inspector from '../inspector/Inspector';
import ReportId from '../report-id/ReportId';
import SubmissionTime from '../submission-time/SubmissionTime';
import UnsafeConditions from '../unsafe-conditions/UnsafeConditions';

export interface InspectionsTableProps {
  data: Array<AppNS.JobEventReport>;
}

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    table: {
      width: '100%',
      '& thead th': {
        padding: '16px 24px',
      },
      '& tbody td': {
        padding: '16px',
      },
      '& tbody td:first-of-type': {
        paddingLeft: '24px',
      },
      '& tbody td:last-child': {
        paddingRight: '24px',
      },
    },
    cellHelper: {
      fontSize: '1.2rem',
    },
    noBreak: {
      whiteSpace: 'nowrap',
    },
    view: {
      background: theme.palette.primary.main,
      padding: '4px',
      height: '26px',
      width: '26px',
      borderRadius: '4px',
      marginRight: '24px',
      color: '#fff',
    },
    tableWrapper: {
      width: '100%',
      background: '#fff',
      overflow: 'auto',
    },
    titleCase: {
      textTransform: 'capitalize',
    },
  };
};

export default function InspectionsTable(props: InspectionsTableProps) {
  const { data } = props;
  const theme = useTheme();
  const styles = getStyles(theme);

  return (
    <Card>
      <Box sx={styles.tableWrapper}>
        <Table sx={styles.table} aria-label="Events table">
          <TableHead>
            <TableRow>
              <TableCell>Event&nbsp;#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Project&nbsp;# / Address</TableCell>
              <TableCell>Inspector</TableCell>
              <TableCell>PIC/MCP</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Permit&nbsp;#</TableCell>
              <TableCell>Geolocation</TableCell>
              <TableCell>Report Submission Time</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item: AppNS.JobEventReport) => (
              <TableRow key={item.id}>
                <TableCell scope="row">
                  <ReportId id={item.id} />
                  <UnsafeConditions data={item} />
                </TableCell>
                <TableCell>
                  {parseDate(item.date, UI_DATE_FORMAT_STRING)}
                  <Box sx={styles.noBreak}>
                    {getHourFormatted(item.timeFrom)} - {getHourFormatted(item.timeTo)}
                  </Box>
                </TableCell>
                <TableCell scope="row">
                  <ExternalButton style={{ textDecoration: 'none' }} to={getProjectExternalLink(item.jobId)}>
                    #{item.jobId}
                  </ExternalButton>
                  <Box sx={styles.titleCase}>{item.address}</Box>
                </TableCell>
                <TableCell>
                  <Inspector item={item} />
                </TableCell>
                <TableCell>{item.picInspector && <>{unescape(item.picInspector.fullName)}</>}</TableCell>
                <TableCell>{item.agencyCompany && <>{unescape(item.agencyCompany.name)}</>}</TableCell>
                <TableCell>{item.client && <>{unescape(item.client.fullName)}</>}</TableCell>
                <TableCell scope="row">{item.permitNumbers && <PermitsInfo permits={item.permitNumbers} />}</TableCell>
                <TableCell scope="row">
                  <Geolocation item={item} />
                </TableCell>
                <TableCell scope="row">
                  <SubmissionTime data={item} />
                </TableCell>
                <TableCell>
                  <ExternalButton to={getInspectionExternalLink(item.id)}>
                    <ViewIcon fontSize="inherit" sx={styles.view} />
                  </ExternalButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}
